import React from "react";
import PropTypes from "prop-types";
import { ACTION_IDS, STATUS_IDS } from "../../../../components/Utils";
import RoleForm from "./RoleForm";
import { Label, InputText } from "../../../../components/Inputs";

function ANPConnectionForm({
  forwardedRef,
  className = "",
  companyData = {
    isContractor: false,
    roleList: [],
    contractGiver: "",
  },
  model = {
    roles: [],
    rolesData: [],
    status: "",
    status_id: 0,
  },
  activeRolesData = [],
  disabled,
}) {
  const rolesFormRef = React.useRef(null);

  const generateState = React.useCallback(() => {
    const data = model || {};
    const status_id =
      data.status_id && data.status_id !== "" ? data.status_id : STATUS_IDS.NEW;
    const rolesData = (
      data.rolesData && Array.isArray(data.rolesData) ? data.rolesData : []
    )
      .filter((role) => {
        return !(role.status_id === STATUS_IDS.DELETING);
      })
      .map((role) => role.name);
    const roles = (
      data.roles && Array.isArray(data.roles) ? data.roles : []
    ).filter((role) => rolesData.includes(role));
    return {
      roles,
      status_id,
      error: {
        roles: null,
      },
    };
  }, [model]);

  const [state, setState] = React.useState(generateState());
  console.log("ANPConnectionForm() model:", model, "\n state:", state);

  React.useEffect(() => {
    setState(generateState());
  }, [generateState]);

  const getEmptyModel = React.useCallback(() => {
    return {
      roles: [],
    };
  }, []);

  const validate = React.useCallback(
    (initialize) => {
      const { error, ...currentModel } = state;
      currentModel.roles = rolesFormRef.current.getModel() || [];
      // role data
      currentModel.rolesData = [];
      currentModel.roles.forEach((roleName) => {
        const roleData = (activeRolesData || []).find(
          (item) => item.name === roleName,
        );
        if (roleData) {
          // roleName unchanged (was present before)
          currentModel.rolesData.push({ ...roleData });
        } else {
          // roleName is new
          currentModel.rolesData.push({
            name: roleName,
            status_id: STATUS_IDS.NEW,
            action_id: ACTION_IDS.CREATE,
          });
        }
      });
      // old roles that have been deleted
      if (activeRolesData) {
        activeRolesData.forEach((oldRole) => {
          if (!currentModel.roles.includes(oldRole.name)) {
            currentModel.rolesData.push({
              ...oldRole,
              status_id: STATUS_IDS.DELETING,
              action_id: ACTION_IDS.DELETE,
            });
          }
        });
      }
      // show error if all roles are deleted
      const hasRoles = currentModel.rolesData.some(
        (role) => role.status_id !== STATUS_IDS.DELETING,
      );
      if (!hasRoles) {
        setState((prev) => ({
          ...prev,
          roles: currentModel.roles,
          error: {
            ...prev.error,
            roles: "Alle Rollen wurden gelöscht",
          },
        }));
        return null;
      }
      if (initialize) {
        setState(getEmptyModel());
      }
      return {
        ...model,
        ...currentModel,
      };
    },
    [state, activeRolesData, getEmptyModel],
  );

  const getModel = React.useCallback(
    (initialize = false) => {
      return validate(initialize);
    },
    [validate],
  );
  React.useImperativeHandle(forwardedRef, () => ({
    getModel,
  }));

  const { isContractor, roleList, contractGiver } = companyData;
  const { roles, error } = state;

  return (
    <div className={className}>
      <div className="form-group-item">
        <Label htmlFor={`contractGiver`}>
          {`Vertragspartner`}
          <span>*</span>
        </Label>
        <InputText
          id={`contractGiver`}
          className="w3-block w3-border pad w3-round"
          value={contractGiver}
          readOnly
        />
      </div>
      <RoleForm
        ref={rolesFormRef}
        key={Math.random()}
        id="roles"
        className="pad-top"
        title="AN-Portal Rollen*"
        model={roles}
        roleList={roleList}
        disabled={disabled}
        isContractor={isContractor}
        min={1}
        error={error.roles}
      />
    </div>
  );
}

ANPConnectionForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    username: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    rolesData: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        status_id: PropTypes.number,
        action_id: PropTypes.number,
      }),
    ),
    status: PropTypes.string,
    status_id: PropTypes.number,
    action_id: PropTypes.number,
  }),
  activeRolesData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      status_id: PropTypes.number,
      action_id: PropTypes.number,
    }),
  ),
  companyData: PropTypes.shape({
    isContractor: PropTypes.bool,
    roleList: PropTypes.array,
    contractGiver: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export default React.forwardRef((props, ref) => (
  <ANPConnectionForm {...props} forwardedRef={ref} />
));
